// apps/admin-ui/src/api/governance.ts
// ALL RIGHTS RESERVED

/**
 * Governance API Client
 *
 * API calls for governance system.
 * Migrated from apps/csr — auth headers adapted to admin-ui pattern.
 *
 * @module apps/admin-ui/api/governance
 */

import { getStoredEmployeeId, getStoredFingerprint, isDevMode, getCurrentEmployee } from '../utils/auth';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface CouncilMember {
  id: string;
  userId: string;
  role: 'council' | 'board' | 'advisor';
  displayName: string;
  canVeto: boolean;
  canCreateProposals: boolean;
  isActive: boolean;
}

export interface Proposal {
  id: string;
  proposalNumber: number;
  title: string;
  description: string;
  proposalType: string;
  createdBy: string;
  creatorName?: string;
  status: 'draft' | 'voting' | 'passed' | 'rejected' | 'vetoed' | 'executed' | 'expired';
  votingStartsAt: string | null;
  votingEndsAt: string | null;
  executableAt: string | null;
  executedAt: string | null;
  executedBy: string | null;
  yesVotes: number;
  noVotes: number;
  vetoVotes: number;
  abstainVotes: number;
  createdAt: string;
  updatedAt: string;
}

export interface Vote {
  id: string;
  proposalId: string;
  memberId: string;
  memberName?: string;
  memberRole?: string;
  vote: 'yes' | 'no' | 'veto' | 'abstain';
  comment: string | null;
  createdAt: string;
}

export interface ProposalEvent {
  id: string;
  proposalId: string;
  eventType: string;
  actorId: string | null;
  actorName?: string;
  eventData: Record<string, unknown>;
  createdAt: string;
}

export interface CurrentMemberInfo {
  member: CouncilMember | null;
  appRole: string | null;
  canVote: boolean;
  canCreate: boolean;
  canVeto: boolean;
}

// ═══════════════════════════════════════════════════════
// API CLIENT
// ═══════════════════════════════════════════════════════

const GOVERNANCE_URL = import.meta.env.VITE_GOVERNANCE_URL || 'http://localhost:3040';

class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // Add auth headers (same pattern as threats.ts)
  const fingerprint = getStoredFingerprint();
  const employeeId = getStoredEmployeeId();

  if (fingerprint) {
    headers['x-client-cert-fingerprint'] = fingerprint;
  } else if (employeeId && isDevMode()) {
    headers['x-dev-employee-id'] = employeeId;
    // Map employee ID to user ID for governance service's requireEmployee middleware
    headers['x-user-id'] = employeeId;
    const employee = getCurrentEmployee();
    if (employee) {
      headers['x-dev-employee-role'] = employee.roles[0] || 'admin';
      headers['x-dev-employee-roles'] = JSON.stringify(employee.roles);
    }
  }

  const response = await fetch(`${GOVERNANCE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new ApiError(
      response.status,
      error.code || 'UNKNOWN_ERROR',
      error.message || 'Request failed'
    );
  }

  return response.json();
}

// ═══════════════════════════════════════════════════════
// COUNCIL ENDPOINTS
// ═══════════════════════════════════════════════════════

export async function getCouncilMembers(): Promise<{
  members: CouncilMember[];
  currentMember: CouncilMember | null;
  totalVoters: number;
}> {
  return request('/internal/governance/council');
}

export async function getCurrentMemberInfo(): Promise<CurrentMemberInfo> {
  return request('/internal/governance/council/me');
}

// ═══════════════════════════════════════════════════════
// PROPOSAL ENDPOINTS
// ═══════════════════════════════════════════════════════

export async function getProposals(params?: {
  status?: string;
  type?: string;
  limit?: number;
  offset?: number;
}): Promise<{
  proposals: Proposal[];
  total: number;
}> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  return request(`/internal/governance/proposals${query ? '?' + query : ''}`);
}

export async function getProposal(id: string): Promise<{
  proposal: Proposal;
  votes: Vote[];
}> {
  return request(`/internal/governance/proposals/${id}`);
}

export async function createProposal(input: {
  title: string;
  description: string;
  proposalType: string;
}): Promise<{ proposal: Proposal }> {
  return request('/internal/governance/proposals', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function submitProposal(id: string): Promise<{ proposal: Proposal }> {
  return request(`/internal/governance/proposals/${id}/submit`, {
    method: 'POST',
  });
}

export async function castVote(
  proposalId: string,
  vote: string,
  comment?: string
): Promise<{ vote: Vote; proposal: { proposal: Proposal; votes: Vote[] } }> {
  return request(`/internal/governance/proposals/${proposalId}/vote`, {
    method: 'POST',
    body: JSON.stringify({ vote, comment }),
  });
}

export async function executeProposal(id: string): Promise<{ proposal: Proposal }> {
  return request(`/internal/governance/proposals/${id}/execute`, {
    method: 'POST',
  });
}

export async function getProposalEvents(id: string): Promise<{ events: ProposalEvent[] }> {
  return request(`/internal/governance/proposals/${id}/events`);
}
